/* eslint-disable no-plusplus */
import { useApiStore } from "@/stores/api/index.ts";
import { Store } from "vuex";
import { ref } from "vue";
import { RootState } from "@/store/types.ts";
import {
  getHostname,
  isLocalHostname,
  isUsingProductionData,
} from "@/util/hostnames.ts";
import { MediaItem } from "@/types/media.ts";
import { generateCsvLinkFromSrc } from "@/util/googleSheetsUtils.ts";
import { RAS_MEDIA_SERVER_HOSTNAME } from "@/util/constants.ts";

export const cacheNames = {
  mediaAssets: "media-assets",
  apiResponses: "api-responses",
  staticFontAssets: "static-font-assets",
  staticImageAssets: "static-image-assets",
  staticJsAssets: "static-js-assets",
  staticStyleAssets: "static-style-assets",
  googleFonts: "google-fonts",
  googleDocs: "google-docs",
  others: "others",
};

// Used for tracking progress and the currently
// downloading URL in ServiceWorkerMonitor.vue
const progress = ref(0);
const currentlyDownloadingUrl = ref("");

function serviceWorkerLog(...args: unknown[]): void {
  console.log("[service worker]", ...args);
}

function serviceWorkerError(...args: unknown[]): void {
  console.error("[service worker]", ...args);
}

function areServiceWorkersSupported(): boolean {
  return "serviceWorker" in navigator;
}

function isServiceWorkerRunning(): boolean {
  return Boolean(navigator.serviceWorker.controller);
}

async function doesMediaUrlExistInCache(url: string): Promise<boolean> {
  if (!window.caches) {
    return false;
  }

  const mediaCacheExists = await window.caches.has(cacheNames.mediaAssets);

  if (mediaCacheExists) {
    const cachedUrl = await window.caches.match(url);

    if (cachedUrl) {
      return true;
    }
  }

  return false;
}

/**
 * Add ability to forcibly enable service worker for debugging purposes,
 * just append ?sw=true to the URL to bypass checks for local or CI.
 */
function shouldDisableServiceWorkerRegistration(): boolean {
  if (window.location.search.includes("sw=true")) {
    return false;
  }

  return import.meta.env.VITE_PLAYWRIGHT_CI || isLocalHostname();
}

/**
 * Add ability to disable service worker if something goes horribly wrong,
 * just append ?sw=false to the URL to unregister the service worker.
 */
function shouldUnregisterServiceWorker(): boolean {
  return window.location.search.includes("sw=false");
}

function isPrefetchingPossible(): boolean {
  if (!navigator?.serviceWorker?.controller) {
    serviceWorkerLog("No service worker controller");
    return false;
  }

  if (!navigator.onLine) {
    serviceWorkerLog("No internet connection");
    return false;
  }

  return true;
}

function isPrefetchingDocumentsEnabled(): boolean {
  return (
    isPrefetchingPossible() && window.location.search.includes("prefetch=true")
  );
}

async function clearAllCaches(): Promise<void> {
  try {
    await caches
      .keys()
      .then((keys) => Promise.all(keys.map((key) => caches.delete(key))));
  } catch (error) {
    serviceWorkerError("Error clearing all caches:", error);
  }
}

async function unregisterServiceWorker(): Promise<void> {
  try {
    await clearAllCaches();

    const registrations = await navigator.serviceWorker.getRegistrations();

    await Promise.all(
      registrations.map((registration) => registration.unregister())
    );
  } catch (error) {
    serviceWorkerError("Error unregistering service worker:", error);
  }
}

async function requestServiceWorkerCache(urls: string[]): Promise<void> {
  for (let i = 0; i < urls.length; i += 1) {
    const currentUrl = urls[i];
    const messageChannel = new MessageChannel();

    serviceWorkerLog("Attempting to cache:", currentUrl);

    currentlyDownloadingUrl.value = currentUrl;

    navigator.serviceWorker.controller?.postMessage(
      {
        type: "CACHE_URLS",
        payload: {
          urlsToCache: [currentUrl],
        },
      },
      [messageChannel.port1]
    );

    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      messageChannel.port2.onmessage = () => {
        progress.value = ((i + 1) / urls.length) * 100;
        messageChannel.port2.close();
        serviceWorkerLog("Cached successfully:", currentUrl, progress.value);
        resolve(true);
      };
    });
  }
}

function generateImageUrls(imageId: string): string[] {
  if (!imageId) {
    return [];
  }

  const baseUrl = `https://${RAS_MEDIA_SERVER_HOSTNAME}/${imageId}/`;

  return [baseUrl];
}

function generateMediaUrl(mediaId: string): string {
  return `https://${RAS_MEDIA_SERVER_HOSTNAME}/${mediaId}/`;
}

function processIframeUrl(iframe: { url: string; sheetName?: string }): string {
  const { url, sheetName } = iframe;

  if (url.includes("docs.google.com/spreadsheets")) {
    return generateCsvLinkFromSrc(url, sheetName);
  }

  return url;
}

function generatePageUrls(
  hostname: string,
  styleId: string,
  path: "home" | "categories"
): string[] {
  const baseUrl = `https://${hostname}/${path}/${styleId}`;
  const queryParams = [
    "",
    "?touchscreen=true",
    "?touchscreen=true&prefetch=true",
    "?prefetch=true&touchscreen=true",
    "?touchscreen=true&prefetch=true&cursor=true",
    "?prefetch=true&cursor=true",
    "?cursor=true",
    "?fromHome=true",
    "?prefetch=true",
    "?backButton=true",
  ];

  return queryParams.map((params) => `${baseUrl}${params}`);
}

async function getAllHomepageVideoUrls(
  store: Store<RootState>
): Promise<string[]> {
  try {
    const videoUrls = store.getters
      .getStoreVar("Home", "styling", "media", "media")
      .filter((item: MediaItem) => item.type === "video" && !item.isExternal)
      .map((item: MediaItem) => item.url);

    return videoUrls;
  } catch (error) {
    serviceWorkerError("Error prefetching homepage videos:", error);
    return [];
  }
}

async function getAllSiteMediaUrls(): Promise<string[]> {
  try {
    const apiStore = useApiStore();

    const { data: touchscreenMedia } = await apiStore.fetchTouchscreenMedia();

    const imageUrls = (touchscreenMedia.images ?? [])
      .map(generateImageUrls)
      .flat();

    const videoUrls = (touchscreenMedia.videos ?? []).map(generateMediaUrl);

    const otherUrls = (touchscreenMedia.other ?? []).map(generateMediaUrl);

    const iframeUrls = (touchscreenMedia.iframes ?? []).map(processIframeUrl);

    return [...imageUrls, ...videoUrls, ...otherUrls, ...iframeUrls];
  } catch (error) {
    serviceWorkerError("Error prefetching site media:", error);
    return [];
  }
}

async function getAllPossibleBackendUrls(
  store: Store<RootState>
): Promise<string[]> {
  try {
    const apiStore = useApiStore();

    const clientId = store.getters.getClientID;
    const styleId = store.getters.getStyleID;
    const calendarLink = store.getters.getStoreVar(
      "Home",
      "styling",
      "text",
      "calendarLink"
    );

    const apiBaseUrl = `https://${
      isUsingProductionData() ? "api." : "dev.api."
    }rocketalumnisolutions.com`;

    const hostname = getHostname();

    const allCategories = await apiStore.fetchAllCategories();
    const categoryUrls = allCategories.data.map(
      (category) =>
        `${apiBaseUrl}/getDocument/${clientId}/category/${category._id}/`
    );

    const homePageUrls = generatePageUrls(hostname, styleId, "home");
    const categoriesPageUrls = generatePageUrls(
      hostname,
      styleId,
      "categories"
    );

    const apiUrls = [
      `${apiBaseUrl}/siteID/${styleId}`,
      `${apiBaseUrl}/searchOptions/${clientId}/`,
      `${apiBaseUrl}/categoryTree/${clientId}/`,
      `${apiBaseUrl}/categoryTree/${clientId}?allCategories=false`,
      `${apiBaseUrl}/allCategories/${clientId}/`,
      `${apiBaseUrl}/getDocument/${clientId}/categories/`,
    ];

    const calendarUrl = calendarLink
      ? `${apiBaseUrl}/calendarData?url=${encodeURIComponent(calendarLink)}`
      : null;

    const allUrls = [
      ...homePageUrls,
      ...categoriesPageUrls,
      ...categoryUrls,
      ...apiUrls,
      ...(calendarUrl ? [calendarUrl] : []),
    ];

    return allUrls;
  } catch (error) {
    serviceWorkerError("Error prefetching API:", error);
    return [];
  }
}

async function prefetch(store: Store<RootState>): Promise<void> {
  const isTouchScreen = store.getters.getTouchScreen;

  if (!isPrefetchingPossible()) {
    serviceWorkerLog("Prefetching not possible, skipping prefetching");
    return;
  }

  if (isPrefetchingDocumentsEnabled() && isTouchScreen) {
    serviceWorkerLog(
      "Prefetching documents enabled, prefetching all site media and API responses"
    );

    const siteMediaUrls = await getAllSiteMediaUrls();
    const backendUrls = await getAllPossibleBackendUrls(store);
    await requestServiceWorkerCache([...siteMediaUrls, ...backendUrls]);
  } else {
    serviceWorkerLog("Prefetching homepage videos only");
    const homepageVideoUrls = await getAllHomepageVideoUrls(store);
    await requestServiceWorkerCache([...homepageVideoUrls]);
  }
}

function onSubsequentActivation(store: Store<RootState>): void {
  serviceWorkerLog("Service worker activated!");
  prefetch(store);
}

function onFirstActivation(store: Store<RootState>): void {
  serviceWorkerLog("Service worker activated for first time (or upgraded)!");
  prefetch(store);
}

export {
  areServiceWorkersSupported,
  isServiceWorkerRunning,
  onFirstActivation,
  onSubsequentActivation,
  serviceWorkerError,
  serviceWorkerLog,
  shouldDisableServiceWorkerRegistration,
  shouldUnregisterServiceWorker,
  unregisterServiceWorker,
  doesMediaUrlExistInCache,
  progress,
  currentlyDownloadingUrl,
};
